import { useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { ArrowLeft, BookOpen, Database, FolderKanban, LayoutDashboard, ListTodo, Network, ScanSearch, Settings2, Target, UsersRound, Workflow, Zap } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ScopeSwitcher from '@/frontend/features/ForgeNavigation/ScopeSwitcher'
import { forgeDestinations, forgeNavigationPath, forgeProjectId } from '@/frontend/layouts/worker-first/forgeNavigation'
import type { SharedProps } from '@/frontend/types/inertia'

const icons = { summary: LayoutDashboard, objectives: Target, tasks: ListTodo, knowledge: BookOpen, people: UsersRound, dashboards: LayoutDashboard, decisions: Target, ontology: Network, data: Database, triggers: Zap, investigations: ScanSearch, workflows: Workflow, settings: Settings2 }
const linkClass = (selected: boolean) => `flex min-h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`

/** Project routes replace the organization menu inside Forge.
 * Both areas share the canonical catalog; detail routes retain their parent.
 * Scrolling belongs to the shell, never to an embedded menu.
 */
export default function ProjectNavigation({ onNavigate }: { onNavigate: () => void }) {
  const { props, url } = usePage<SharedProps>()
  const copy = props.navigationLabels.forge
  const projectId = forgeProjectId(url, props.forgeAccess?.default_project_id)
  const active = forgeNavigationPath(url)
  const activeArea = forgeDestinations.find((item) => item.path === active)?.group
  const [area, setArea] = useState(activeArea === 'technical' ? 'technical' : 'project')
  useEffect(() => {
    if (activeArea !== 'settings') setArea(activeArea === 'technical' ? 'technical' : 'project')
  }, [url, projectId, activeArea])
  const destinations = forgeDestinations.filter((item) => item.key !== 'objectives' || (props.featureFlags?.outcome_beta_enabled && props.featureFlags.outcome_beta_project_id === projectId))
  const renderLinks = (group: 'project' | 'technical') => <nav aria-label={group === 'project' ? copy.management : copy.build} className="space-y-0.5">
    {destinations.filter((item) => item.group === group).map((item) => {
      const Icon = icons[item.key]
      const dashboards = item.key === 'dashboards'
      const selected = dashboards ? /^\/dashboard\/projects\/[^/]+\/dashboards(?:\/|\?|$)/.test(url) : active === item.path
      const href = dashboards ? `/dashboard/projects/${projectId}/dashboards` : `${item.path}?project_id=${projectId}`
      return <Link key={item.key} href={href} onClick={onNavigate} aria-current={selected ? 'page' : undefined} className={linkClass(selected)}>
        <Icon className="size-3.5 shrink-0" strokeWidth={1.75} /><span>{copy[item.key]}</span>
      </Link>
    })}
  </nav>

  return <section data-testid="forge-project-navigation" className="shrink-0 px-2.5 pt-1">
    <Link href="/dashboard" onClick={onNavigate} className="mb-3 flex min-h-8 items-center gap-2 px-2 text-xs text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-3.5" />{copy.back_to_organization}
    </Link>
    {projectId ? <>
      <ScopeSwitcher compact onNavigate={onNavigate} />
      <Tabs value={area} onValueChange={setArea} className="mt-1">
        <TabsList aria-label={copy.project_areas} className="mb-2 grid h-8 w-full grid-cols-2 rounded-md bg-muted/60 p-0.5">
          <TabsTrigger value="project" className="rounded-[4px] text-xs">{copy.management}</TabsTrigger>
          <TabsTrigger value="technical" className="rounded-[4px] text-xs">{copy.build}</TabsTrigger>
        </TabsList>
        <TabsContent value="project" className="mt-0">{renderLinks('project')}</TabsContent>
        <TabsContent value="technical" className="mt-0">{renderLinks('technical')}</TabsContent>
      </Tabs>
      <div className="mt-3 border-t border-border pt-2">
        <Link href={`/dashboard/discovery/settings?project_id=${projectId}`} onClick={onNavigate} aria-current={active === '/dashboard/discovery/settings' ? 'page' : undefined} className={linkClass(active === '/dashboard/discovery/settings')}>
          <Settings2 className="size-3.5" strokeWidth={1.75} />{copy.settings}
        </Link>
      </div>
    </> : <>
      <h2 className="px-2.5 pb-3 text-sm font-semibold">{props.navigationLabels.discovery}</h2>
      <Link href="/dashboard/discovery/projects" onClick={onNavigate} aria-current="page" className={linkClass(true)}>
        <FolderKanban className="size-3.5" />{copy.all_projects}
      </Link>
    </>}
  </section>
}
